import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { useTheme } from '../../../contexts/ThemeContext';

const StatsCounter = () => {
  const { isDarkMode } = useTheme();
  
  const stats = [
    {
      id: 1,
      icon: 'Users',
      number: '1,000+',
      label: 'Happy Customers',
      description: 'Satisfied clients across Coimbatore',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      highlightBg: 'bg-blue-600'
    },
    {
      id: 2,
      icon: 'MapPin',
      number: '5+', 
      label: 'Service Points',
      comingSoon: 'Coming Soon',
      description: 'Convenient locations citywide',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      iconBg: 'bg-green-100',
      highlightBg: 'bg-green-600'
    },
    {
      id: 3,
      icon: 'Clock',
      number: '24hrs',
      label: 'Quick Service',
      description: 'Fast turnaround time',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      highlightBg: 'bg-orange-600'
    },
    {
      id: 4,
      icon: 'Award',
      number: '5 Years',
      label: 'Experience',
      description: 'Trusted laundry expertise',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      highlightBg: 'bg-purple-600'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className={`py-12 sm:py-16 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
        : 'bg-gradient-to-br from-gray-50 to-white'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Trusted by Thousands
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${
            isDarkMode ? 'text-slate-300' : 'text-gray-600'
          }`}>
            Our numbers speak for themselves - join the growing community of satisfied customers
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.id}
              variants={itemVariants}
              whileHover={{ 
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              className="group relative"
            >
              <div className={`${
                isDarkMode 
                  ? (stat.id === 1 ? 'bg-gradient-to-br from-blue-900/20 to-slate-800/50 border-blue-700/30' : 
                     stat.id === 2 ? 'bg-gradient-to-br from-green-900/20 to-slate-800/50 border-green-700/30' : 
                     stat.id === 3 ? 'bg-gradient-to-br from-orange-900/20 to-slate-800/50 border-orange-700/30' : 
                     'bg-gradient-to-br from-purple-900/20 to-slate-800/50 border-purple-700/30')
                  : `${stat.bgColor} border-gray-200/50`
              } rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border h-full relative overflow-hidden`}>
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className={`absolute top-4 right-4 w-20 h-20 rounded-full blur-2xl ${
                    isDarkMode ? 
                      (stat.id === 1 ? 'bg-blue-500' : 
                       stat.id === 2 ? 'bg-green-500' : 
                       stat.id === 3 ? 'bg-orange-500' : 
                       'bg-purple-500')
                      : 'bg-gray-400'
                  }`}></div>
                  <div className={`absolute bottom-4 left-4 w-16 h-16 rounded-full blur-xl ${
                    isDarkMode ? 
                      (stat.id === 1 ? 'bg-blue-400' : 
                       stat.id === 2 ? 'bg-green-400' : 
                       stat.id === 3 ? 'bg-orange-400' : 
                       'bg-purple-400')
                      : 'bg-gray-300'
                  }`}></div>
                </div>

                {/* Icon */}
                <div className="relative z-10 flex justify-end mb-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 ${
                    isDarkMode 
                      ? (stat.id === 1 ? 'bg-blue-500/20' : 
                         stat.id === 2 ? 'bg-green-500/20' : 
                         stat.id === 3 ? 'bg-orange-500/20' : 
                         'bg-purple-500/20')
                      : stat.iconBg
                  } rounded-xl flex items-center justify-center`}>
                    <Icon 
                      name={stat.icon} 
                      size={20} 
                      className={isDarkMode ? 
                        (stat.id === 1 ? 'text-blue-400' : 
                         stat.id === 2 ? 'text-green-400' : 
                         stat.id === 3 ? 'text-orange-400' : 
                         'text-purple-400') 
                        : stat.color}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="relative z-10 space-y-3">
                  <div className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${
                    isDarkMode ? 'text-white' : 'text-gray-800'
                  }`}>
                    {stat.number}
                    {stat.comingSoon && (
                      <span className="ml-2 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">
                        {stat.comingSoon}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className={`inline-block ${
                      isDarkMode 
                        ? (stat.id === 1 ? 'bg-blue-600 text-blue-100' : 
                           stat.id === 2 ? 'bg-green-600 text-green-100' : 
                           stat.id === 3 ? 'bg-orange-600 text-orange-100' : 
                           'bg-purple-600 text-purple-100')
                        : `${stat.highlightBg} text-white`
                    } px-2 py-1 rounded-lg text-xs sm:text-sm font-semibold`}>
                      {stat.label}
                    </div>
                    <p className={`text-xs sm:text-sm ${
                      isDarkMode ? 'text-slate-400' : 'text-gray-600'
                    }`}>
                      {stat.description}
                    </p>
                  </div>
                </div>

                {/* Hover Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  isDarkMode 
                    ? (stat.id === 1 ? 'from-blue-500/20 to-transparent' : 
                       stat.id === 2 ? 'from-green-500/20 to-transparent' : 
                       stat.id === 3 ? 'from-orange-500/20 to-transparent' : 
                       'from-purple-500/20 to-transparent')
                    : 'from-white/20 to-transparent'
                } rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <div className={`flex items-center justify-center space-x-2 ${
            isDarkMode ? 'text-green-400' : 'text-green-600'
          }`}>
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Icon name="TrendingUp" size={20} />
            </motion.div>
            <span className="text-sm font-medium">
              Growing every day with your trust
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StatsCounter;